import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Globe, X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Public hosted portfolio view — reachable at /p/:userId/:slug WITHOUT
 * authentication. Loads a slim JSON payload from `/api/portfolios/public/...`
 * and renders a magazine-style layout with an optional AI-generated hero
 * cover, a per-project image collage, and a full-screen lightbox for
 * inspecting individual images. Supports a NL/EN language toggle that
 * translates the static UI labels (project content stays verbatim).
 */
const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// Static UI labels — only the chrome around user content translates.
const LABELS = {
  nl: {
    portfolioEyebrow: "Portfolio",
    projectNoun: "Project",
    empty: "Nog geen projecten toegevoegd.",
    respLabel: "Verantwoordelijkheden",
    skillsLabel: "Top gerelateerde vaardigheden",
    notFoundTitle: "Portfolio niet gevonden",
    notFoundBody: "Deze link is verlopen of het portfolio is verwijderd.",
    loading: "Laden…",
    serverError: "Er ging iets mis bij het laden.",
    madeWith: "Gemaakt met JobGrid ·",
    langLabel: "Taal",
  },
  en: {
    portfolioEyebrow: "Portfolio",
    projectNoun: "Project",
    empty: "No projects added yet.",
    respLabel: "Responsibilities",
    skillsLabel: "Top related skills",
    notFoundTitle: "Portfolio not found",
    notFoundBody: "This link has expired or the portfolio was removed.",
    loading: "Loading…",
    serverError: "Something went wrong while loading.",
    madeWith: "Made with JobGrid ·",
    langLabel: "Language",
  },
};

/**
 * Bare hostnames (`mijnsite.nl`) become relative hrefs and 404 on the
 * public page. Force `https://` when no scheme is present. Also handled
 * server-side, but we mirror it here for older rows persisted before the
 * fix.
 */
function absoluteUrl(u) {
  if (!u) return "";
  const s = String(u).trim();
  if (!s) return "";
  if (/^[a-zA-Z][a-zA-Z0-9+.\-]*:/.test(s)) return s;
  return "https://" + s.replace(/^\/+/, "");
}

/**
 * Choose a grid-tile class per image based on the total count so the
 * collage never looks like a boring 2-col grid. Layouts (tile index →
 * col/row span) are curated per image count for a magazine feel.
 *   1 image  → 1 hero tile (col-span-6 row-span-2)
 *   2 images → 2 equal tiles side by side
 *   3 images → 1 wide hero + 2 stacked
 *   4 images → 2 large + 2 small
 *   5 images → 1 hero + 4 supporting
 *   6 images → mosaic (2 large + 4 small)
 */
const COLLAGE_LAYOUTS = {
  1: ["col-span-6 row-span-2"],
  2: ["col-span-3 row-span-2", "col-span-3 row-span-2"],
  3: ["col-span-6 row-span-2", "col-span-3 row-span-1", "col-span-3 row-span-1"],
  4: [
    "col-span-4 row-span-2",
    "col-span-2 row-span-1",
    "col-span-2 row-span-1",
    "col-span-4 row-span-1",
  ],
  5: [
    "col-span-4 row-span-2",
    "col-span-2 row-span-1",
    "col-span-2 row-span-1",
    "col-span-3 row-span-1",
    "col-span-3 row-span-1",
  ],
  6: [
    "col-span-3 row-span-2",
    "col-span-3 row-span-1",
    "col-span-3 row-span-1",
    "col-span-2 row-span-1",
    "col-span-2 row-span-1",
    "col-span-2 row-span-1",
  ],
};

function Collage({ urls, onOpen }) {
  const layout = COLLAGE_LAYOUTS[Math.min(urls.length, 6)] || [];
  if (urls.length === 0) return null;
  return (
    <div
      className="grid grid-cols-6 auto-rows-[130px] sm:auto-rows-[170px] gap-3 mt-6"
      data-testid="portfolio-item-collage"
    >
      {urls.slice(0, 6).map((u, i) => (
        <button
          key={u + i}
          type="button"
          onClick={() => onOpen(i)}
          className={`${layout[i] || "col-span-3 row-span-1"} group relative overflow-hidden border border-neutral-200 bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-neutral-900`}
          data-testid={`portfolio-item-collage-tile-${i}`}
        >
          <img
            src={`${API}${u}`}
            alt=""
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
        </button>
      ))}
    </div>
  );
}

function Lightbox({ urls, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    if (index === null) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onNext();
      else if (e.key === "ArrowLeft") onPrev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [index, onClose, onPrev, onNext]);

  if (index === null) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="portfolio-lightbox"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
        aria-label="close"
        data-testid="portfolio-lightbox-close"
      >
        <X size={22} strokeWidth={1.5} />
      </button>
      {urls.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-4 sm:left-8 text-white/80 hover:text-white p-2"
            data-testid="portfolio-lightbox-prev"
          >
            <ChevronLeft size={26} strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-4 sm:right-8 text-white/80 hover:text-white p-2"
            data-testid="portfolio-lightbox-next"
          >
            <ChevronRight size={26} strokeWidth={1.5} />
          </button>
        </>
      )}
      <img
        src={`${API}${urls[index]}`}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="max-w-[95vw] max-h-[90vh] object-contain shadow-2xl"
        data-testid="portfolio-lightbox-image"
      />
      <p className="absolute bottom-4 left-0 right-0 text-center text-white/70 font-mono text-[0.65rem] uppercase tracking-[0.15em]">
        {index + 1} / {urls.length}
      </p>
    </div>
  );
}

function LangSwitcher({ lang, onChange }) {
  return (
    <div
      className="inline-flex items-stretch border border-neutral-300 bg-white/70 backdrop-blur"
      role="group"
      aria-label="Language"
      data-testid="portfolio-lang-switcher"
    >
      {[
        { code: "nl", label: "NL" },
        { code: "en", label: "EN" },
      ].map(({ code, label }) => (
        <button
          key={code}
          type="button"
          onClick={() => onChange(code)}
          aria-pressed={lang === code}
          data-testid={`portfolio-lang-${code}`}
          className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] px-2.5 py-1.5 transition-colors ${
            lang === code
              ? "bg-neutral-900 text-[#F9F8F6]"
              : "bg-transparent text-neutral-700 hover:text-neutral-900"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function PublicPortfolio() {
  const { userId, slug } = useParams();
  const [state, setState] = useState({ portfolio: null, items: [], loading: true, error: null });
  // Lightbox holds one flat list of URLs for the active project so
  // arrow-navigation stays inside that project's collage.
  const [lb, setLb] = useState({ urls: [], index: null });

  // Language toggle — synced with `?lang=en` in the URL so shared
  // links can be pre-set to the visitor's preferred language.
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = useMemo(() => {
    const q = (searchParams.get("lang") || "").toLowerCase();
    return q === "en" ? "en" : "nl";
  }, [searchParams]);
  const L = LABELS[lang] || LABELS.nl;
  const setLang = useCallback((code) => {
    const next = new URLSearchParams(searchParams);
    if (code === "nl") next.delete("lang");
    else next.set("lang", code);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    axios
      .get(`${API}/api/portfolios/public/${userId}/${slug}`)
      .then((r) => setState({ portfolio: r.data.portfolio, items: r.data.items || [], loading: false, error: null }))
      .catch((err) => setState({ portfolio: null, items: [], loading: false, error: err?.response?.status === 404 ? "not_found" : "server" }));
  }, [userId, slug]);

  const openLb = useCallback((urls, index) => setLb({ urls, index }), []);
  const closeLb = useCallback(() => setLb({ urls: [], index: null }), []);
  const prevLb = useCallback(() => setLb((s) => ({ ...s, index: (s.index - 1 + s.urls.length) % s.urls.length })), []);
  const nextLb = useCallback(() => setLb((s) => ({ ...s, index: (s.index + 1) % s.urls.length })), []);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center">
        <p className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-500">{L.loading}</p>
      </div>
    );
  }
  if (state.error === "not_found") {
    return (
      <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <p className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">404</p>
          <h1 className="font-serif text-3xl">{L.notFoundTitle}</h1>
          <p className="text-sm text-neutral-600 mt-2">{L.notFoundBody}</p>
        </div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center">
        <p className="text-sm text-rose-700">{L.serverError}</p>
      </div>
    );
  }

  const { portfolio, items } = state;
  const coverUrl = portfolio.cover_image_url ? `${API}${portfolio.cover_image_url}` : null;

  return (
    <div className="min-h-screen bg-[#FAF7F1] text-neutral-900" data-testid="public-portfolio">
      {/* Floating language switcher — pinned top-right so it's reachable
          on every scroll position without competing with the hero copy. */}
      <div className="fixed top-4 right-4 z-40">
        <LangSwitcher lang={lang} onChange={setLang} />
      </div>

      {/* Cover — AI-generated hero image overlaid with title (when
          available) or a plain dark banner as fallback. */}
      {coverUrl ? (
        <header className="relative overflow-hidden" data-testid="public-portfolio-cover">
          <img
            src={coverUrl}
            alt=""
            className="w-full h-[42vh] sm:h-[56vh] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F10]/85 via-[#0F0F10]/25 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-4xl mx-auto w-full px-6 sm:px-10 pb-8 sm:pb-14 text-[#FAF7F1]">
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-[#D9A441] mb-3">{L.portfolioEyebrow}</p>
              <h1 className="font-serif text-4xl sm:text-6xl leading-none">{portfolio.name}</h1>
              {portfolio.tagline && <p className="mt-4 text-lg text-neutral-200 max-w-2xl leading-relaxed">{portfolio.tagline}</p>}
            </div>
          </div>
        </header>
      ) : (
        <header className="bg-[#0F0F10] text-[#FAF7F1] px-6 py-14 sm:px-10 sm:py-20">
          <div className="max-w-4xl mx-auto">
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-[#D9A441] mb-3">{L.portfolioEyebrow}</p>
            <h1 className="font-serif text-4xl sm:text-6xl leading-none">{portfolio.name}</h1>
            {portfolio.tagline && <p className="mt-4 text-lg text-neutral-300 max-w-2xl leading-relaxed">{portfolio.tagline}</p>}
          </div>
        </header>
      )}

      {/* Projects */}
      <main className="max-w-4xl mx-auto px-6 sm:px-10 py-12 space-y-16">
        {items.length === 0 && (
          <p className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-500 text-center py-12">
            {L.empty}
          </p>
        )}
        {items.map((it, i) => {
          const website = absoluteUrl(it.website_url);
          let host = website;
          try { host = new URL(website).hostname.replace(/^www\./, ""); } catch { /* keep raw */ }
          const urls = it.image_urls || [];
          return (
            <article key={it.id} data-testid={`public-item-${i}`} className="border-t border-neutral-200 pt-8">
              <p className="font-mono text-[0.55rem] uppercase tracking-[0.2em] text-neutral-500">
                {L.projectNoun} {String(i + 1).padStart(2, "0")}
              </p>
              <h2 className="font-serif text-3xl mt-2 leading-tight">{it.project_name}</h2>
              {website && (
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 mt-2"
                  data-testid={`public-item-link-${i}`}
                >
                  <Globe size={12} strokeWidth={1.5} /> {host}
                </a>
              )}
              <Collage urls={urls} onOpen={(idx) => openLb(urls, idx)} />
              {it.description && (
                <p className="mt-6 text-neutral-800 leading-relaxed whitespace-pre-wrap text-lg">{it.description}</p>
              )}
              {it.responsibilities && (
                <div className="mt-6" data-testid={`public-item-resp-${i}`}>
                  <p className="font-mono text-[0.55rem] uppercase tracking-[0.2em] text-neutral-500 mb-2">
                    {L.respLabel}
                  </p>
                  <p className="text-neutral-800 leading-relaxed whitespace-pre-wrap text-base">
                    {it.responsibilities}
                  </p>
                </div>
              )}
              {Array.isArray(it.skills) && it.skills.length > 0 && (
                <div className="mt-6" data-testid={`public-item-skills-${i}`}>
                  <p className="font-mono text-[0.55rem] uppercase tracking-[0.2em] text-neutral-500 mb-2">
                    {L.skillsLabel}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {it.skills.map((s) => (
                      <span
                        key={s}
                        className="inline-flex items-center border border-neutral-400 text-neutral-800 px-2.5 py-1 text-[0.78rem] leading-tight"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </main>

      <footer className="max-w-4xl mx-auto px-6 sm:px-10 py-10 border-t border-neutral-200">
        <p className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-neutral-500">
          {L.madeWith} <a href="/" className="hover:text-neutral-900">jobgrid.nl</a>
        </p>
      </footer>

      <Lightbox
        urls={lb.urls}
        index={lb.index}
        onClose={closeLb}
        onPrev={prevLb}
        onNext={nextLb}
      />
    </div>
  );
}
